// Thème partagé par les graphiques du haut du dashboard bâtiment (donut de
// répartition, sunbursts, barchart coût, camembert coût, conso/personne).
// Diagrammes circulaires : chaque graphique suit un dégradé, échantillonné sur le
// nombre réel de parts (gradientColors) pour toujours en couvrir toute l'étendue. Les
// sous-catégories éclaircissent la teinte de leur catégorie (subcategoryColor), jamais
// via alpha, pour rester des aplats solides — lisibles et fiables sur n'importe quel export.
// Dégradé chaud : "Répartition des consommations" (donut du haut).
export const CONSUMPTION_GRADIENT = ['#febf00', '#eb7246', '#c52330', '#bc356a', '#895273'];
// Dégradé froid : sunbursts usages/sous-usages et sections/pièces.
export const BREAKDOWN_GRADIENT = ['#fce071', '#6fa89a', '#205673', '#31356e', '#895273'];
export const MAX_SLICES = 9;
export const OTHER_COLOR = '#98A2B3';

export const SURFACE_LOWEST = '#ffffff';
export const DARK_TEXT = '#181c22';
export const MUTED_TEXT = '#5a5e68';
export const LIGHT_TEXT = '#ffffff';
export const DIM_TARGET = '#dde0ea'; // gris neutre proche de --color-surface-high, utilisé pour estomper un segment non sélectionné
export const FONT_STACK = "'Fira Sans', sans-serif";

export const formatValue = (value) => {
    if (value === undefined || value === null) return '';
    return value.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
};

// `round` arrondit à l'unité de pourcentage la plus proche — sauf en dessous de 1%, où
// la décimale est gardée (un "0%" pour un usage réel, même minime, serait trompeur).
export const formatPercent = (value, round) => {
    if (round && Math.abs(value) >= 1) return Math.round(value).toString();
    return value.toFixed(1).replace('.', ',');
};

export const withHexAlpha = (hex, alpha) => {
    const a = Math.round(alpha * 255).toString(16).padStart(2, '0');
    return `${hex}${a}`;
};

export const hexToRgb = (hex) => {
    const h = hex.replace('#', '');
    return [0, 2, 4].map((i) => parseInt(h.substring(i, i + 2), 16));
};

export const rgbToHex = ([r, g, b]) =>
    '#' + [r, g, b].map((c) => Math.max(0, Math.min(255, Math.round(c))).toString(16).padStart(2, '0')).join('');

// Mélange opaque (pas d'alpha) vers une couleur cible — utilisé pour les teintes de
// sous-catégories (vers blanc) et pour l'estompage au survol (vers gris neutre).
export const mixColor = (hex, targetHex, ratio) => {
    const a = hexToRgb(hex);
    const b = hexToRgb(targetHex);
    return rgbToHex(a.map((c, i) => c + (b[i] - c) * ratio));
};

const rgbToHsl = ([r, g, b]) => {
    const [rn, gn, bn] = [r / 255, g / 255, b / 255];
    const max = Math.max(rn, gn, bn);
    const min = Math.min(rn, gn, bn);
    const l = (max + min) / 2;
    if (max === min) return [0, 0, l];
    const d = max - min;
    const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    let h;
    if (max === rn) h = (gn - bn) / d + (gn < bn ? 6 : 0);
    else if (max === gn) h = (bn - rn) / d + 2;
    else h = (rn - gn) / d + 4;
    return [h / 6, s, l];
};

const hslToRgb = ([h, s, l]) => {
    if (s === 0) return [l * 255, l * 255, l * 255];
    const hue = (p, q, t) => {
        const tt = t < 0 ? t + 1 : t > 1 ? t - 1 : t;
        if (tt < 1 / 6) return p + (q - p) * 6 * tt;
        if (tt < 1 / 2) return q;
        if (tt < 2 / 3) return p + (q - p) * (2 / 3 - tt) * 6;
        return p;
    };
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    return [hue(p, q, h + 1 / 3), hue(p, q, h), hue(p, q, h - 1 / 3)].map((c) => c * 255);
};

// sRGB <-> OKLab : interpoler dans un espace perceptuel évite les milieux de dégradé
// ternes (jaune → bleu passerait sinon par un gris-vert boueux).
const srgbToLinear = (c) => {
    const v = c / 255;
    return v <= 0.04045 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
};
const linearToSrgb = (c) => 255 * (c <= 0.0031308 ? 12.92 * c : 1.055 * Math.pow(c, 1 / 2.4) - 0.055);

const hexToOklab = (hex) => {
    const [r, g, b] = hexToRgb(hex).map(srgbToLinear);
    const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
    const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
    const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);
    return [
        0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s,
        1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s,
        0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s,
    ];
};

const oklabToHex = ([L, A, B]) => {
    const l = Math.pow(L + 0.3963377774 * A + 0.2158037573 * B, 3);
    const m = Math.pow(L - 0.1055613458 * A - 0.0638541728 * B, 3);
    const s = Math.pow(L - 0.0894841775 * A - 1.291485548 * B, 3);
    return rgbToHex(
        [
            4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
            -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
            -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s,
        ].map((c) => linearToSrgb(Math.max(0, c)))
    );
};

// `count` couleurs réparties régulièrement le long des points d'arrêt `stops`.
export const gradientColors = (stops, count) => {
    const labs = stops.map(hexToOklab);
    return Array.from({ length: count }, (_, i) => {
        const t = count <= 1 ? 0 : i / (count - 1);
        const x = t * (labs.length - 1);
        const k = Math.min(Math.floor(x), labs.length - 2);
        const f = x - k;
        return oklabToHex(labs[k].map((v, j) => v + (labs[k + 1][j] - v) * f));
    });
};

// Couleur de chaque entrée d'une liste ordonnée : `isOther(item)` désigne les entrées
// hors dégradé ("Autres", "Non catégorisé"), en gris neutre ; les autres se partagent
// le dégradé dans leur ordre d'affichage.
export const colorsAlongGradient = (items, stops, isOther) => {
    const colors = gradientColors(stops, items.filter((item) => !isOther(item)).length);
    let next = 0;
    return items.map((item) => (isOther(item) ? OTHER_COLOR : colors[next++]));
};

// Teinte d'une sous-catégorie : on éclaircit la couleur de sa catégorie en HSL (même
// teinte) en ne retirant qu'une partie de la saturation, au lieu d'un mélange vers le
// blanc qui grisait les couleurs sombres et délavait les claires. `index`/`count`
// placent la sous-catégorie sur la rampe, de la plus proche de la catégorie à la plus claire.
const SUBCATEGORY_TARGET_LIGHTNESS = 0.85;
export const subcategoryColor = (baseHex, index, count) => {
    const t = count <= 1 ? 0.5 : 0.25 + (index / (count - 1)) * 0.5; // 0.25 -> 0.75
    const [h, s, l] = rgbToHsl(hexToRgb(baseHex));
    const lightness = l < SUBCATEGORY_TARGET_LIGHTNESS ? l + (SUBCATEGORY_TARGET_LIGHTNESS - l) * t : l;
    return rgbToHex(hslToRgb([h, s * (1 - 0.6 * t), lightness]));
};

export const relativeLuminance =([r, g, b]) => {
    const channel = (v) => {
        const c = v / 255;
        return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    };
    const [rl, gl, bl] = [r, g, b].map(channel);
    return 0.2126 * rl + 0.7152 * gl + 0.0722 * bl;
};

export const contrastRatio = (l1, l2) => {
    const [a, b] = l1 > l2 ? [l1, l2] : [l2, l1];
    return (a + 0.05) / (b + 0.05);
};

const DARK_LUM = relativeLuminance(hexToRgb(DARK_TEXT));
const LIGHT_LUM = relativeLuminance(hexToRgb(LIGHT_TEXT));

// Choisit blanc ou gris foncé selon le meilleur ratio de contraste WCAG.
export const contrastTextColor = (hex) => {
    const bgLum = relativeLuminance(hexToRgb(hex));
    const rWhite = contrastRatio(bgLum, LIGHT_LUM);
    const rDark = contrastRatio(bgLum, DARK_LUM);
    return rWhite >= rDark ? LIGHT_TEXT : DARK_TEXT;
};

// Regroupe les catégories excédentaires (au-delà de MAX_SLICES) dans une entrée "Autres".
export const foldIntoOthers = (labels, values) => {
    if (labels.length <= MAX_SLICES) return { labels, values };

    const paired = labels.map((label, i) => ({ label, value: values[i] }));
    paired.sort((a, b) => b.value - a.value);

    const kept = paired.slice(0, MAX_SLICES - 1);
    const rest = paired.slice(MAX_SLICES - 1);
    const othersTotal = rest.reduce((sum, item) => sum + item.value, 0);

    return {
        labels: [...kept.map((item) => item.label), 'Autres'],
        values: [...kept.map((item) => item.value), othersTotal],
    };
};

export const polarToCartesian = (cx, cy, r, angle) => ({ x: cx + r * Math.sin(angle), y: cy - r * Math.cos(angle) });

// Répartit une colonne de labels au plus près de leur position naturelle, en imposant
// un espacement vertical minimal (2 passes : pousse vers le bas, puis retasse).
export const distributeNatural = (items, gap) => {
    if (items.length === 0) return items;
    const sorted = [...items].sort((a, b) => a.y - b.y);
    for (let i = 1; i < sorted.length; i++) {
        if (sorted[i].y - sorted[i - 1].y < gap) sorted[i].y = sorted[i - 1].y + gap;
    }
    for (let i = sorted.length - 2; i >= 0; i--) {
        if (sorted[i + 1].y - sorted[i].y < gap) sorted[i].y = sorted[i + 1].y - gap;
    }
    return sorted;
};

// Répartit une colonne de labels à intervalle parfaitement régulier, centrée sur la
// moyenne de leurs positions naturelles.
export const distributeEqually = (items, gap) => {
    if (items.length === 0) return items;
    const sorted = [...items].sort((a, b) => a.y - b.y);
    const centerY = sorted.reduce((sum, it) => sum + it.y, 0) / sorted.length;
    const startY = centerY - ((sorted.length - 1) * gap) / 2;
    return sorted.map((it, i) => ({ ...it, y: startY + i * gap }));
};

// Libellés/couleurs d'affichage d'un point d'eau (équipement + note de rating) —
// partagés entre ConsumptionBreakdownDetail.js et UsageTaxonomySunburstCard.js, deux
// endroits qui listent des points d'eau individuels avec le même habillage visuel.
export const EQUIPMENT_TYPE_LABELS = {
    faucet: 'Robinet / Lavabo',
    wc: 'WC',
    urinal: 'Urinoir',
    shower: 'Douche',
    machine: 'Machine',
};

export const RATING_LETTER_COLORS = {
    A: '#00bf63',
    B: '#c7de26',
    C: '#f6cc27',
    D: '#fc7c2c',
    E: '#ff5a3d',
    F: '#ff1b3b',
    G: '#c0003f',
};

export const generateLegendData = (chartData, total) => {
    const dataset = chartData.datasets[0];
    return chartData.labels
        .map((label, i) => {
            const value = dataset.data[i];
            const percentage = ((value / total) * 100).toFixed(1);
            if (percentage <= 0.01) return null; // Exclude zero values
            return {
                label: `${label} (${percentage}%)`,
                rawLabel: label,
                backgroundColor: dataset.backgroundColor[i],
                borderColor: dataset.borderColor[i],
            };
        })
        .filter((item) => item !== null); // Remove null entries
};
